import {
  NativeStackHeaderProps,
  Stack,
  useFocusEffect,
  useLocalSearchParams,
  useRouter,
} from 'expo-router';
import { getDcfButtonAnalytics } from 'features/moveMoney/screens/dcf/helpers/DcfAnalytics.helpers';
import useTabapayTransfer from 'features/moveMoney/screens/dcf/hooks/useTabaPayTransfer';
import useTransferLimitations from 'features/moveMoney/screens/dcf/hooks/useTransferLimitations';
import {
  getFlowNameForAnalytics,
  TabaPayEntryFlowType,
} from 'features/moveMoney/screens/dcf/tabapay/helpers/TabaPay.helpers';
import TabapayTransferErrorScreen from 'features/moveMoney/screens/dcf/tabapay/TabapayTransfer/components/TabapayTransferErrorScreen';
import { useTabapayErrorHandlers } from 'features/moveMoney/screens/dcf/tabapay/TabapayTransfer/hooks/useTabapayErrorHandlers';
import { SourceScreen } from 'features/myVaro/screens/ExternalAccounts/hooks/useUpdateExternalCardExpiryDate';
import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ScrollView, StyleSheet, View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { VaroPressableNames } from 'shared/analytics/types/pressableNames';
import AmountInput, {
  AmountInputRef,
} from 'shared/components/vxl/AmountInput/AmountInput';
import BottomSheet from 'shared/components/vxl/BottomSheet/BottomSheet';
import Button from 'shared/components/vxl/Button/Button';
import TransferField from 'shared/components/vxl/fields/TransferField/TransferField';
import Icon from 'shared/components/vxl/icon/Icon/Icon';
import IconButton from 'shared/components/vxl/icon/IconButton/IconButton';
import FullScreenLoading from 'shared/components/vxl/loading/FullScreenLoading/FullScreenLoading';
import ExpoNavigationBar from 'shared/components/vxl/navigation/ExpoNavigationBar/ExpoNavigationBar';
import Snackbar from 'shared/components/vxl/Snackbar/Snackbar';
import BodyBaseText from 'shared/components/vxl/typography/primary/body/BodyBaseText';
import TitleMdText from 'shared/components/vxl/typography/primary/title/TitleMdText';
import { isIOS, isWeb } from 'shared/constants/platform';
import { SNACKBAR_DURATION } from 'shared/constants/snackbar';
import { usePressableAnalytics } from 'shared/hooks/analytics/usePressableAnalytics';
import { useViewDidFocusScreenAnalyticsEvent } from 'shared/hooks/analytics/useViewDidFocusAnalytics';
import ScreenTransitionOverlay from 'shared/patterns/vxl/ScreenTransitionOverlay/ScreenTransitionOverlay';
import { VXLComponentId } from 'shared/renderer/types';
import theme from 'shared/styles/theme';
import useVaroOnboardingFlow from 'shared/utils/useVaroOnboardingFlow/useVaroOnboardingFlow';

const TabapayTransferScreen = () => {
  const router = useRouter();
  const { t: translatedText } = useTranslation('moveMoney', {
    keyPrefix: 'transferStrings',
  });
  const { bottom } = useSafeAreaInsets();

  const { fromFundInstantly, isBelieveDay0Flow } = useLocalSearchParams<{
    fromFundInstantly?: string;
    isBelieveDay0Flow?: string;
  }>();

  const {
    checkingAccount,
    firstExternalCard,
    transferAmount,
    setTransferAmount,
    handleSubmit,
    bottomSheetRef,
    ignoreOnChange,
    setIgnoreOnChange,
    handleErrorButtonPress,
    cardId,
    combinedErrorScreen,
    combinedLoading,
    shouldShowBackButton,
    updatedMessage,
    setUpdatedMessage,
    transactionId,
    cancelTabapayTransfer,
    errorThreeDsWeb,
    errorThreeDsHandler,
    sourceName,
    checkAndDeleteCard,
    monthlyLimitInformation,
    dailyLimitInformation,
    disclaimer,
    limitErrorMessage,
    maxAmount,
    minAmount,
    isEligible,
    eligibleErrorScreen,
    isProcessing,
    markPartialAuthClose,
  } = useTabapayTransfer();

  const [isAmountTouched, setIsAmountTouched] = useState(false);
  const handleAmountBlur = () => setIsAmountTouched(true);

  const {
    isSubmitDisabled: isSubmitButtonDisabled,
    transferLimitations,
    transferLimitationsIcon,
    transferLimitationsTextColor,
  } = useTransferLimitations({
    transferAmount,
    maxAmount,
    minAmount,
    limitErrorMessage,
  });

  const flowName = getFlowNameForAnalytics(TabaPayEntryFlowType.DCFFlow);
  // Show caption when amount is touched, has value, or there's a limit error
  const showAmountCaption =
    isAmountTouched || Number(transferAmount) > 0 || Boolean(limitErrorMessage);
  const isBD0Flow = isBelieveDay0Flow === 'true';
  const analyticsFlowName = isBD0Flow
    ? 'dcf_believe_day_0_application'
    : flowName;
  const amountInputRef = React.useRef<AmountInputRef>(null);

  const flow = useVaroOnboardingFlow();

  const fromAccountClickedAnalytics = usePressableAnalytics({
    analyticsId: 'selectAccount',
    customAttributes: {
      direction: 'from',
      flow_name: analyticsFlowName,
    },
  });

  const toAccountClickedAnalytics = usePressableAnalytics({
    analyticsId: 'selectAccount',
    customAttributes: {
      direction: 'to',
      flow_name: analyticsFlowName,
    },
  });

  const fromAccountSelectedAnalytics = usePressableAnalytics({
    analyticsId: 'accountSelected',
    customAttributes: {
      direction: 'from',
      card_type: 'debit_card',
      institution_name: firstExternalCard?.institution?.name,
      flow_name: analyticsFlowName,
    },
  });

  const toAccountSelectedAnalytics = usePressableAnalytics({
    analyticsId: 'accountSelected',
    customAttributes: {
      direction: 'to',
      account_type: 'checking',
      institution_name: checkingAccount?.institution?.name,
      flow_name: analyticsFlowName,
    },
  });

  const addMoneyClicked = usePressableAnalytics({
    analyticsId: 'addMoneyClicked',
    customAttributes: {
      flow_name: analyticsFlowName,
    },
  });

  useEffect(() => {
    if (firstExternalCard && checkingAccount) {
      fromAccountSelectedAnalytics();
      toAccountSelectedAnalytics();
    }
  }, [
    checkingAccount,
    firstExternalCard,
    fromAccountSelectedAnalytics,
    toAccountSelectedAnalytics,
  ]);

  useViewDidFocusScreenAnalyticsEvent({
    key: 'tabapay-transfer-screen',
    customAttributes: { flow_name: analyticsFlowName },
  });

  useFocusEffect(
    useCallback(() => {
      if (
        !combinedLoading &&
        !combinedErrorScreen &&
        !eligibleErrorScreen &&
        firstExternalCard &&
        checkingAccount
      ) {
        amountInputRef.current?.focus();
      }
    }, [
      combinedLoading,
      combinedErrorScreen,
      eligibleErrorScreen,
      firstExternalCard,
      checkingAccount,
    ]),
  );

  const onPressInfoIcon = () => {
    router.push({
      pathname: `/${flow}/tabapay/limits`,
      params: {
        dailyLimitTitle: dailyLimitInformation?.title,
        dailyLimitMessage1: dailyLimitInformation?.message1,
        dailyLimitMessage2: dailyLimitInformation?.message2,
        monthlyLimitTitle: monthlyLimitInformation?.title,
        monthlyLimitMessage1: monthlyLimitInformation?.message1,
        monthlyLimitMessage2: monthlyLimitInformation?.message2,
        disclaimer,
      },
    });
  };

  const errorScreenConfig = useTabapayErrorHandlers({
    cardFormError: {
      combinedErrorScreen: null,
      errorScreenWeb: null,
      errorScreenThreeDs: null,
      setShouldRefreshCards: () => {},
      cancelTabapayTransfer,
      transactionId,
      checkAndDeleteCard,
      translatedText,
      handleErrorPrimaryButtonOnPress: () => {},
      handleErrorButtonPress: () => {},
      cancelTransferLoading: false,
    },
    transferError: {
      combinedErrorScreen: combinedErrorScreen,
      errorThreeDsWeb,
      errorThreeDsHandler,
      cancelTabapayTransfer,
      transactionId,
      checkAndDeleteCard,
      sourceName,
      translatedText,
      handleErrorButtonPress: handleErrorButtonPress,
      onPartialAuthClose: markPartialAuthClose,
    },
    eligibility: {
      isEligible,
      eligibleErrorScreen,
    },
    flowName,
    isProcessing,
  });

  const errorComponent = errorScreenConfig ? (
    <TabapayTransferErrorScreen {...errorScreenConfig} />
  ) : null;

  const isWebErrorVisible = isWeb && !!errorScreenConfig;
  const titleKey =
    fromFundInstantly === 'true' ? 'fundInstantlyTitle' : 'title';
  const shouldShowHeader =
    shouldShowBackButton && isEligible && !isWebErrorVisible;
  const shouldShowBackArrow = shouldShowBackButton && !isWebErrorVisible;

  return (
    <>
      <Stack.Screen
        options={{
          title: translatedText(titleKey),
          headerShown: shouldShowHeader,
          headerBackVisible: shouldShowBackArrow,
          header: (props: NativeStackHeaderProps) => (
            <ExpoNavigationBar
              trailingAccessories={
                <View style={styles.buttonWrapper}>
                  <IconButton
                    iconKey="INFO"
                    iconSize="MD"
                    iconButtonAccessibilityLabel={translatedText(
                      'infoIconLabel',
                    )}
                    iconOnPress={onPressInfoIcon}
                  />
                </View>
              }
              {...props}
            />
          ),
        }}
      />
      <ScreenTransitionOverlay
        isLoading={combinedLoading}
        LoadingComponent={<FullScreenLoading isCoinLoader />}
        ErrorComponent={errorComponent}
      />

      {firstExternalCard && checkingAccount ? (
        <ScrollView contentContainerStyle={styles.scrollStyle}>
          <TransferField
            primaryText={`${firstExternalCard.institution.name} • ${firstExternalCard.maskedCardNumber}`}
            secondaryText={firstExternalCard.cardNetwork}
            labelText={translatedText('from')}
            avatarAccessory={{
              name: VXLComponentId.ICON_AVATAR,
              id: 'transfer-money-source-varo-logo',
              props: {
                iconAccessibilityLabel: 'logo',
                imageSource: { uri: firstExternalCard.iconUrl },
                variant: 'MD',
              },
            }}
            showChevron={false}
            onPress={() => {
              fromAccountClickedAnalytics();
            }}
          />
          <TransferField
            primaryText={`${checkingAccount.nickname} • ${checkingAccount.accountNumberLast4}`}
            secondaryText={
              isBD0Flow
                ? translatedText('movedToBelieveInstantly')
                : `${translatedText('available')} ${checkingAccount.availableBalance?.formatted}`
            }
            labelText={translatedText('to')}
            showChevron={false}
            avatarAccessory={{
              name: VXLComponentId.ICON_AVATAR,
              id: 'transfer-money-destination-varo-logo',
              props: {
                iconAccessibilityLabel: 'logo',
                imageSource: { uri: checkingAccount.iconUrl },
                variant: 'MD',
              },
            }}
            onPress={() => {
              toAccountClickedAnalytics();
            }}
          />
          <View style={styles.transferAmountContainer}>
            <AmountInput
              ref={amountInputRef}
              value={transferAmount}
              captionText={showAmountCaption ? transferLimitations : undefined}
              onChangeText={setTransferAmount}
              onBlur={handleAmountBlur}
              prefix="$"
              captionTextIcon={
                showAmountCaption ? transferLimitationsIcon : undefined
              }
              captionTextColor={
                showAmountCaption ? transferLimitationsTextColor : undefined
              }
              // Disable amount entry when the risk service returns a limit error
              // so the user cannot submit an out-of-policy amount.
              isDisabled={Boolean(limitErrorMessage)}
            />
          </View>
          <View
            style={[
              styles.buttonContainer,
              {
                paddingBottom: bottom,
              },
            ]}
          >
            <Button
              label={translatedText('addMoney')}
              onPress={async () => {
                addMoneyClicked();
                setTransferAmount(transferAmount);
                await handleSubmit();
              }}
              isDisabled={isSubmitButtonDisabled}
              isLoading={false}
              size="LARGE"
              type="PRIMARY"
              width="FULL"
              customAttributes={{ amount: transferAmount }}
              // addMoneyClicked() above emits legacy `Transfer Money Clicked`, but only on the
              // move-money, onboarding and believe-funding routes — progress-tracker-funding has no config.json key.
              componentAnalytics={getDcfButtonAnalytics({
                pressableName: VaroPressableNames.DCF.DCF_ADD_MONEY_SAVED_CARD,
                isBelieveDay0Flow: isBD0Flow,
              })}
            />
          </View>
        </ScrollView>
      ) : null}
      {updatedMessage ? (
        <Snackbar
          text={updatedMessage}
          duration={SNACKBAR_DURATION}
          onHide={() => setUpdatedMessage('')}
        />
      ) : null}

      <BottomSheet
        ref={bottomSheetRef}
        onChange={(index) => {
          if (index === -1) {
            if (ignoreOnChange) {
              setIgnoreOnChange(false);
              return;
            }
            router.back();
          }
        }}
      >
        <View style={styles.parentContainer}>
          <View>
            <Icon iconKey="ERROR" iconSize={100} />
          </View>
          <View style={styles.textContainer}>
            <TitleMdText style={styles.text}>
              {translatedText('bottomSheet.title')}
            </TitleMdText>
            <BodyBaseText style={styles.text}>
              {translatedText('bottomSheet.subTitle')}
            </BodyBaseText>
          </View>
          <View style={styles.bottomsheetButtonContainer}>
            <Button
              label={translatedText('bottomSheet.updateButton')}
              type="PRIMARY"
              onPress={() => {
                router.push({
                  pathname: '/myvaro/external-accounts/update-expiry-date',
                  params: {
                    cardId: cardId,
                    from: SourceScreen.TRANSFER_SCREEN,
                    tabaPayEntryFlowType: TabaPayEntryFlowType.DCFFlow,
                  },
                });
                setIgnoreOnChange(true);
                bottomSheetRef?.current?.dismiss();
              }}
            />
            <Button
              label={translatedText('bottomSheet.closeButton')}
              type="SECONDARY"
              onPress={() => {
                bottomSheetRef?.current?.dismiss();
              }}
            />
          </View>
        </View>
      </BottomSheet>
    </>
  );
};

const styles = StyleSheet.create({
  bottomsheetButtonContainer: {
    backgroundColor: theme.color.background.primary,
    marginHorizontal: theme.spacing.m,
    rowGap: theme.spacing.m,
    width: '100%',
  },
  buttonContainer: {
    backgroundColor: theme.color.background.primary,
    marginBottom: isIOS ? theme.spacing.xs : theme.spacing.m,
    marginHorizontal: theme.spacing.m,
  },
  buttonWrapper: { height: 20 },
  parentContainer: {
    alignItems: 'center',
    gap: theme.spacing.m,
    justifyContent: 'center',
    paddingBottom: theme.spacing.m,
    paddingHorizontal: theme.spacing.m,
  },
  scrollStyle: {
    backgroundColor: theme.color.background.primary,
    flexGrow: 1,
    justifyContent: 'space-between',
  },
  text: {
    justifyContent: 'center',
    textAlign: 'center',
  },
  textContainer: {
    gap: theme.spacing.s,
    paddingVertical: theme.spacing.m,
  },
  transferAmountContainer: {
    flex: 1,
    height: '100%',
    marginTop: theme.spacing.l,
    paddingHorizontal: theme.spacing.m,
    paddingTop: theme.spacing.xxl,
  },
});

export default TabapayTransferScreen;
